/**
 * Deterministic Project Intent / Overview prose for the report section.
 * Used as fallback when LLM-authored `projectIntentOverview` is absent.
 */
import type { ReportTypeId } from '../config/reportTypes';
import type { PowerAvailabilityPreferences, ProjectIntent } from '../types/siteRag';
import type { SiteRagReport } from '../types/siteRag';
import { analysisReadinessDecisionV1Schema } from '../types/decisionAnalysis';
import { summarizeProjectIntent } from './projectIntentSummary';
import { humanizeIntentSummary } from './intentContextFormat';
import { REACTOR_DESIGN_V1_SCOPE_NOTE, intentShowsReactorDesignPicker } from '../config/reactorDesignCatalog';
import {
  filterRenderableParagraphs,
  isRenderableNarrative,
  safeExecSummarySnippet,
  stripAppOnboardingCopy,
} from './safeNarrativeProse';

import { isScreeningRecommendationMode } from './intent/screeningIntentMode';
import {
  derivePresentedScreeningRecommendationSetV1,
  deriveScreeningRecommendationsForPresentation,
  presentScreeningRecommendationFields,
} from './findings/screeningRecommendationsForPresentation';
import { SCREENING_ARCHITECTURE_LINE_IDS } from './findings/screeningArchitectureRecommendations';

export type OverviewRecommendationState =
  | 'recommended'
  | 'conditional'
  | 'withheld'
  | 'unresolved';

export interface ProjectIntentOverviewInput {
  siteName: string;
  address?: string;
  projectIntent?: ProjectIntent;
  executiveSummarySnippet?: string;
  reportType?: ReportTypeId | null;
  powerSetup?: PowerAvailabilityPreferences;
  recommendationState?: OverviewRecommendationState;
}

interface TypeFraming {
  /** Closing sentence after the intent summary. */
  closing: string;
}

const TYPE_FRAMING: Record<ReportTypeId, TypeFraming> = {
  feasibility: {
    closing: 'All feasibility scoring, behind-the-meter technology comparisons, and development pathway recommendations in this document are framed against these goals.',
  },
  environmental: {
    closing: 'Permitting pathway screening, ecological assessments, and mitigation recommendations in this environmental diligence report are framed against these deployment goals.',
  },
  'power-availability': {
    closing: 'Grid capacity analysis, interconnection queue review, and BTM technology comparisons in this power-availability report are framed against these goals.',
  },
  'water-resource': {
    closing: 'Water supply, rights, drought resilience, and cooling-demand sections in this water-resource report are framed against these goals.',
  },
  'test-fit': {
    closing: 'Parcel layout, setback compliance, and buildout capacity sections in this test-fit report are framed against these goals.',
  },
  interconnection: {
    closing: 'Fiber availability, latency modeling, and connectivity risk sections in this interconnection report are framed against these goals.',
  },
  'supply-chain-risk': {
    closing: 'Procurement posture, lead-time critical path, and supplier risk sections in this supply-chain diligence report are framed against these goals.',
  },
  'custom-brief': {
    closing: 'Diligence Q&A responses in this custom brief are framed against these goals.',
  },
  'public-sentiment': {
    closing: 'Stakeholder mapping, sentiment themes, and mitigation playbook sections in this public sentiment report are framed against these goals.',
  },
  'executive-summary': {
    closing: 'Cross-report synthesis, BTM viability assessment, and recommended next steps in this executive brief are framed against these goals.',
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report framing.
  financial: {
    closing: 'LCOE, NPV / IRR-vs-hurdle, payback, Monte-Carlo probability, and the pathway-economics comparison in this financial viability report are framed against these goals.',
  },
};

function resolveReportType(reportType: ReportTypeId | null | undefined): ReportTypeId {
  return reportType ?? 'feasibility';
}

function buildPowerSetupParagraph(prefs: PowerAvailabilityPreferences, projectIntent?: ProjectIntent): string | null {
  const parts: string[] = [];
  const mw = projectIntent?.powerRequirementMW;
  if (typeof mw === 'number' && Number.isFinite(mw)) {
    parts.push(`target capacity ${mw} MW`);
  }
  if (prefs.btmStrategy) parts.push(`BTM strategy ${prefs.btmStrategy}`);
  if (prefs.renewablesPreference) parts.push(`renewables preference ${prefs.renewablesPreference}`);
  if (prefs.curtailmentRiskTolerance) parts.push(`curtailment tolerance ${prefs.curtailmentRiskTolerance}`);
  if (prefs.timeToPowerCriticalDate) parts.push(`time-to-power critical date ${prefs.timeToPowerCriticalDate}`);
  if (typeof prefs.maxSubstationDistanceMiAmber === 'number') {
    parts.push(`substation amber threshold ${prefs.maxSubstationDistanceMiAmber} mi`);
  }
  if (typeof prefs.maxSubstationDistanceMiRed === 'number') {
    parts.push(`substation red threshold ${prefs.maxSubstationDistanceMiRed} mi`);
  }
  if (parts.length === 0) return null;
  return `Power report setup: ${parts.join('; ')}. Subsequent grid and BTM sections apply these constraints.`;
}

function buildRecommendModeStatusSentence(
  state: OverviewRecommendationState | undefined,
  hasSummary: boolean,
): string {
  if (state === 'recommended') {
    return hasSummary
      ? 'Saved screening recommendations for capacity envelope, power delivery architecture, and cooling strategy are summarized above and remain advisory pending utility and engineering verification.'
      : 'Saved screening recommendations for capacity range, power delivery architecture, and cooling strategy are summarized above and remain advisory pending utility and engineering verification.';
  }
  if (state === 'conditional') {
    return 'Partial screening recommendations are summarized above alongside unresolved dimensions and evidence gaps; unresolved dimensions are not filled from declared inputs.';
  }
  if (state === 'withheld') {
    return 'Screening architecture recommendations are withheld under a quality or source-admission hold; declared inputs are recorded separately and are not treated as recommendations.';
  }
  return 'No current saved screening architecture recommendation is established in this revision; open capacity, power delivery, and cooling dimensions remain subject to the evidence gaps identified in this screening, and declared inputs are recorded separately.';
}

/** Expand the compact intent chip string into 1–2 readable paragraphs. */
export function buildProjectIntentOverview(input: ProjectIntentOverviewInput): string | null {
  const {
    siteName,
    address,
    projectIntent,
    executiveSummarySnippet,
    powerSetup,
    recommendationState,
  } = input;
  const reportType = resolveReportType(input.reportType);
  const framing = TYPE_FRAMING[reportType];
  const summary = summarizeProjectIntent(projectIntent);
  const recommendMode = isScreeningRecommendationMode(projectIntent);
  if (!summary && !address && !recommendMode) return null;

  const paragraphs: string[] = [];

  if (recommendMode) {
    const statusSentence = buildRecommendModeStatusSentence(
      recommendationState,
      Boolean(summary),
    );
    if (summary) {
      paragraphs.push(
        `This report screens ${siteName}${address ? ` at ${address}` : ''} in Recommend-an-Approach mode against the declared project intent: ${summary}. ${statusSentence} `
        + framing.closing,
      );
    } else {
      paragraphs.push(
        `This report screens ${siteName}${address ? ` at ${address}` : ''} in Recommend-an-Approach mode (open-brief discovery). ${statusSentence} `
        + framing.closing,
      );
    }
    if (projectIntent && intentShowsReactorDesignPicker(projectIntent)) {
      paragraphs.push(REACTOR_DESIGN_V1_SCOPE_NOTE);
    }
  } else if (summary) {
    const modePrefix = projectIntent?.screeningMode === 'evaluate-proposal'
      ? `This report evaluates ${siteName}${address ? ` at ${address}` : ''} in Evaluate-My-Proposal mode against the declared project intent: ${summary}. `
      : `This report evaluates ${siteName}${address ? ` at ${address}` : ''} against the declared project intent: ${summary}. `;
    paragraphs.push(modePrefix + framing.closing);
    if (projectIntent && intentShowsReactorDesignPicker(projectIntent)) {
      paragraphs.push(REACTOR_DESIGN_V1_SCOPE_NOTE);
    }
  } else if (address) {
    // Redhead review wave 3 (defect 7) — the report body carries ONLY the
    // factual evaluate sentence. The "Set Project Intent in site
    // configuration…" call-to-action is app onboarding copy and must never
    // ship inside report prose (in-app empty states keep their own copy).
    paragraphs.push(`This report evaluates ${siteName} at ${address}.`);
  }

  if (reportType === 'power-availability' && powerSetup) {
    const setupPara = buildPowerSetupParagraph(powerSetup, projectIntent);
    if (setupPara) paragraphs.push(setupPara);
  }

  if (executiveSummarySnippet && executiveSummarySnippet.trim().length > 40) {
    if (isRenderableNarrative(executiveSummarySnippet)) {
      const trimmed = executiveSummarySummaryTrim(executiveSummarySnippet);
      if (trimmed && isRenderableNarrative(trimmed)) {
        paragraphs.push(trimmed);
      }
    }
  }

  return paragraphs.length > 0 ? paragraphs.join('\n\n') : null;
}

/** Normalized paragraph key for the cover-brief / caveat de-dup (citation
 *  tokens + whitespace + trailing punctuation stripped, lowercased). */
function overviewParaKey(s: string): string {
  return s
    .trim()
    .replace(/<!--\s*confidence:[^>]*-->/gi, '')
    .replace(/\[[^\]]*\bRef\b[^\]]*\]|\[\d+\]/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/[.,;:\s]+$/, '')
    .toLowerCase();
}

function deriveOverviewRecommendationState(
  report: Parameters<typeof resolveProjectIntentOverviewProse>[0],
  sourceDocument = false,
): OverviewRecommendationState {
  const fullReport = report as SiteRagReport;
  if (
    fullReport.qualityHold === 'needs-revision' ||
    (fullReport.qualityHoldFindings?.length ?? 0) > 0
  ) {
    return 'withheld';
  }
  // A canonical source is frozen before its new immutable revision pointers
  // exist. Missing those derived pointers is not evidence that its recorded
  // recommendation set is empty. Read only that same-report frozen set here;
  // re-admit all supporting fields, and never relax saved-result eligibility
  // for normal display callers.
  if (sourceDocument && !fullReport.reportRevision) {
    const set = fullReport.screeningRecommendationSetV1;
    if (
      fullReport._metadataOnly || fullReport.isDraft || fullReport.generationTier === 'draft' ||
      (fullReport.reportType ?? 'feasibility') !== 'feasibility' ||
      set?.schemaVersion !== 'screening-recommendation-set-v1' ||
      set.reportId !== fullReport.id || set.screeningMode !== 'recommend' ||
      !set.questions || typeof set.questions !== 'object' || Array.isArray(set.questions)
    ) return 'unresolved';
    if (set.sourceState === 'quality-hold' || set.sourceState === 'readiness-blocked') return 'withheld';
    if (set.sourceState !== 'ready') return 'unresolved';
    const readiness = fullReport.analysisReadiness ?? fullReport.analysisRevision?.readiness;
    if (readiness != null) {
      const parsed = analysisReadinessDecisionV1Schema.safeParse(readiness);
      if (!parsed.success || !parsed.data.canView || !parsed.data.identityValid
        || !parsed.data.qualityReady || !parsed.data.narrativeReady
        || !((parsed.data.ready && parsed.data.canUseForDecision)
          || parsed.data.state === 'queued' || parsed.data.state === 'refreshing')) return 'withheld';
    }
    if (!SCREENING_ARCHITECTURE_LINE_IDS.every(id => {
      const q = set.questions[id];
      return q && typeof q === 'object' && !Array.isArray(q)
        && q.questionId === id && q.reportId === fullReport.id
        && ['recommended', 'alternatives', 'unresolved', 'not-produced', 'withheld-source'].includes(q.status)
        && typeof q.fieldPath === 'string'
        && typeof q.basisNote === 'string'
        && (q.value == null || typeof q.value === 'string')
        && (q.unresolvedReason == null || typeof q.unresolvedReason === 'string')
        && Array.isArray(q.citations) && q.citations.every(citation => typeof citation === 'string')
        && (q.alternatives == null || (Array.isArray(q.alternatives)
          && q.alternatives.every(alternative => typeof alternative === 'string')));
    })) return 'unresolved';
    const questions = Object.values(set.questions);
    if (questions.length !== SCREENING_ARCHITECTURE_LINE_IDS.length) return 'unresolved';
    if (questions.some(q => q.status === 'withheld-source' || !presentScreeningRecommendationFields(
      fullReport,
      [q.value ?? '', q.basisNote, q.unresolvedReason ?? '', ...(q.alternatives ?? []), q.citations.join(' ')],
    ))) return 'withheld';
    const hasValue = (id: keyof typeof set.questions): boolean => {
      const q = set.questions[id];
      return Boolean(q && (q.status === 'recommended' || q.status === 'alternatives') && q.value?.trim());
    };
    const allCoreResolved = hasValue('dc-power')
      && (hasValue('btm-architecture') || hasValue('power-type'))
      && (hasValue('cooling-strategy') || hasValue('cooling-type'));
    if (allCoreResolved) return 'recommended';
    return questions.some(q => (q.status === 'recommended' || q.status === 'alternatives') && q.value?.trim())
      ? 'conditional' : 'unresolved';
  }
  const savedRecs = deriveScreeningRecommendationsForPresentation(fullReport, fullReport.siteId);
  const savedSet = derivePresentedScreeningRecommendationSetV1(fullReport, fullReport.siteId);
  if (
    savedSet.sourceState === 'quality-hold' ||
    savedSet.sourceState === 'readiness-blocked' ||
    Object.values(savedSet.questions).some((q) => q.status === 'withheld-source')
  ) {
    return 'withheld';
  }
  if (savedSet.sourceState === 'ready') {
    const hasSavedRecs = Object.keys(savedRecs).length > 0;
    const allCoreResolved =
      Boolean(savedRecs['dc-power']) &&
      Boolean(savedRecs['btm-architecture'] || savedRecs['power-type']) &&
      Boolean(savedRecs['cooling-strategy'] || savedRecs['cooling-type']);
    if (hasSavedRecs && allCoreResolved) return 'recommended';
    if (hasSavedRecs) return 'conditional';
  }
  return 'unresolved';
}

/**
 * OVERVIEW-CLEANUP (William 2026-07-04) — the reader-facing post-process for the
 * project-intent overview prose, run at the resolve boundary so it heals BOTH
 * the LLM-authored prose AND the deterministic fallback (and every legacy
 * persisted report, no regen):
 *   1. INTENT-HUMANIZE-1 — the machine intent summary the LLM echoed after
 *      "declared project intent:" is rewritten to reader prose (no "OR:/AND:"
 *      editor prefixes, no repeated utility clause, ISO dates → "Dec 2027").
 *   2. BRIEF-DEDUP-1 — the executive-summary lede is rendered IN FULL as the
 *      cover "IN BRIEF"; a paragraph here that repeats it verbatim is dropped
 *      (the cover is the single home for the brief).
 *   3. the standalone "Generic design class only…" v1-scope caveat is dropped
 *      from the overview (it reads as a dangling note; the reactor scope belongs
 *      to the BTM technology section).
 *   4. RECOMMEND-STATE-1 — legacy recommend-mode sentences that claim capacity,
 *      power, and cooling are recommended from verified site evidence are
 *      reconciled against the report's actual recommendation state.
 */
function cleanupOverviewProse(
  prose: string,
  execSummary: string | undefined,
  projectIntent?: ProjectIntent,
  legacySummary?: string,
  recommendationState?: OverviewRecommendationState,
): string {
  const humanized = prose
    .replace(
      /(declared project intent:\s*)([^.]*?)(\.\s)/,
      (_m, pre: string, summary: string, post: string) =>
        pre + (humanizeIntentSummary(summary, projectIntent) || summary) + post,
    )
    .replace(
      /(?:Remaining capacity envelope, power delivery architecture, and cooling strategy are recommended from verified site evidence\.|Recommended approach options are derived from verified site evidence above; unresolved dimensions are not filled from declared inputs\.|Saved screening recommendations for capacity (?:envelope|range), power delivery architecture, and cooling strategy are summarized above and remain advisory pending utility and engineering verification\.|Partial screening recommendations are summarized above alongside unresolved dimensions and evidence gaps; unresolved dimensions are not filled from declared inputs\.|Screening architecture recommendations are withheld under a quality or source-admission hold; declared inputs are recorded separately and are not treated as recommendations\.|No current saved screening architecture recommendation is established in this revision; open capacity, power delivery, and cooling dimensions remain subject to the evidence gaps identified in this screening, and declared inputs are recorded separately\.)/g,
      buildRecommendModeStatusSentence(recommendationState, true),
    )
    .replace(
      /\s+to identify the recommended screening capacity range, behind-the-meter or grid power architecture, and cooling strategy grounded in verified site evidence\./g,
      `. ${buildRecommendModeStatusSentence(recommendationState, false)}`,
    );
  const reactorKey = overviewParaKey(REACTOR_DESIGN_V1_SCOPE_NOTE);
  // Reference labels may differ, but additional or different citations are
  // unique evidence and must survive prose deduplication.
  const citationKey = (text: string): string =>
    (text.match(/\[[^\]]*\bRef\b[^\]]*\]|\[\d+\]/gi) ?? [])
      .map((token) => token
        .replace(/\b(?:verified|inferred|speculative)\s+(?=Ref\b)/gi, '')
        .replace(/\s+/g, ' ')
        .toLowerCase())
      .join('|');
  const execKeys = [
    ...(execSummary ? execSummary.split(/\n\n+/) : []),
    ...(legacySummary ? legacySummary.split(/\n\n+/) : []),
  ]
    .map((paragraph) => ({ prose: overviewParaKey(paragraph).trim(), citations: citationKey(paragraph) }))
    .filter((key) => key.prose.length > 40);
  const kept = humanized
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => {
      const k = overviewParaKey(p).trim();
      if (k === reactorKey) return false;
      // Only complete duplicates belong exclusively to the cover. Containment
      // can hide unique constraints, qualifications, or supporting citations.
      if (execKeys.some((ek) => ek.prose === k && ek.citations === citationKey(p))) return false;
      return true;
    });
  return kept.join('\n\n');
}

/** Resolve overview prose for render/export — filters leaks and rebuilds when needed. */
export function resolveProjectIntentOverviewProse(
  report: Pick<
    SiteRagReport,
    | 'projectIntentOverview'
    | 'siteName'
    | 'address'
    | 'projectIntentAtGen'
    | 'executiveSummary'
    | 'reportType'
    | 'sectionGuardStatus'
    | 'typedPreferences'
  > & { summary?: string },
  options: { sourceDocument?: boolean } = {},
): string | null {
  const authored = report.projectIntentOverview?.trim();
  const guard = report.sectionGuardStatus?.projectIntentOverview;
  const recommendationState = isScreeningRecommendationMode(report.projectIntentAtGen)
    ? deriveOverviewRecommendationState(report, options.sourceDocument)
    : undefined;

  if (authored && guard !== 'degraded-instruction-leak') {
    // Redhead review wave 3 (defect 7) — legacy generated reports persisted
    // the "Set Project Intent in site configuration…" app-onboarding CTA into
    // the overview prose; strip it at the resolve boundary so no existing
    // report renders/exports it (regeneration not required).
    const filtered = stripAppOnboardingCopy(filterRenderableParagraphs(authored));
    const bareAddressStub = `This report evaluates ${report.siteName}${report.address ? ` at ${report.address}` : ''}.`;
    const isBareRecommendStub = isScreeningRecommendationMode(report.projectIntentAtGen)
      && filtered.trim() === bareAddressStub;
    if (filtered && !isBareRecommendStub) {
      const cleaned = cleanupOverviewProse(
        filtered,
        report.executiveSummary,
        report.projectIntentAtGen,
        report.summary,
        recommendationState,
      );
      if (cleaned) return cleaned;
    }
  }

  const fallback = buildProjectIntentOverview({
    siteName: report.siteName,
    address: report.address,
    projectIntent: report.projectIntentAtGen,
    executiveSummarySnippet: safeExecSummarySnippet(report),
    reportType: report.reportType,
    powerSetup: report.reportType === 'power-availability'
      ? report.typedPreferences?.['power-availability']
      : undefined,
    recommendationState,
  });
  return fallback
    ? cleanupOverviewProse(
        fallback,
        report.executiveSummary,
        report.projectIntentAtGen,
        report.summary,
        recommendationState,
      ) || null
    : null;
}

function executiveSummarySummaryTrim(text: string): string | null {
  const first = text.trim().split(/\n\n+/)[0]?.trim();
  if (!first || first.length < 40) return null;
  return first;
}
